import { nextSprint } from '@nedelnik/shared';
import { useCallback, useEffect, useRef, useState } from 'react';

import { haptic } from '../haptic.js';

import type { SprintKey, Task } from '@nedelnik/shared';

/**
 * Свайп строки списка вбок (3.1.12): перенос между неделей и Входящими.
 *
 * Своё, а не из `@dnd-kit`: там перетаскивание, а здесь жест, у которого
 * нет цели, только направление и порог. С захватом долгим нажатием он не
 * спорит: захвату нужны полсекунды неподвижного пальца, и сдвиг больше восьми
 * пикселей его отменяет, а свайп начинается с десяти.
 *
 * Слушатели касаний — родные, не React: прокрутку страницы, пока строка едет
 * вбок, можно погасить только `preventDefault` в `touchmove`, а React вешает
 * этот слушатель пассивным.
 */

export type SwipeSide = 'left' | 'right';

/** Куда уедет задача: в спринт (`null` — Входящие) или в корзину. */
export type SwipeTarget = { kind: 'move'; sprint: SprintKey | null } | { kind: 'trash' };

export type SwipeTargets = Partial<Record<SwipeSide, SwipeTarget>>;

/**
 * Что делает свайп с этой задачей (решение владельца 2026-10-17).
 *
 * В неделе вправо — в следующий спринт, влево — во Входящие: вправо — дальше
 * во времени, влево — назад в неразобранное. Во Входящих вправо — в
 * показанный спринт (там это текущий), влево — в корзину: Входящие копят
 * идеи, которые потом не нужны (3.1.13, решение владельца 2026-10-18).
 * У закрытой задачи действий в строке нет (3.1.9) — нет и свайпа.
 */
export function swipeTargets(task: Task, shownSprint: SprintKey, done: boolean): SwipeTargets {
  if (done) return {};
  if (task.sprint === null) {
    return { right: { kind: 'move', sprint: shownSprint }, left: { kind: 'trash' } };
  }
  return {
    right: { kind: 'move', sprint: nextSprint(task.sprint) },
    left: { kind: 'move', sprint: null },
  };
}

/** Сдвиг, после которого жест считается свайпом, а не дрожью пальца. */
const LOCK_PX = 10;

/**
 * Порог срабатывания — треть строки, но не меньше 80 пикселей. Меньше —
 * переносы случались бы от небрежной прокрутки; больше — палец не дотягивался
 * бы одной рукой.
 */
export function commitDistance(width: number): number {
  return Math.max(80, width * 0.35);
}

/**
 * Сдвиг строки за пальцем. В сторону, где действия нет, строка тянется
 * четвертью хода и не дальше 40 пикселей: видно, что жест понят, но
 * отвечать ему нечем.
 */
export function swipeOffset(dx: number, allowed: boolean): number {
  if (allowed) return dx;
  return Math.sign(dx) * Math.min(Math.abs(dx) * 0.25, 40);
}

/** Возврат на место и уход за край — с одной скоростью. */
export const SETTLE_MS = 180;

/**
 * Сколько ждать, пока ушедшая строка исчезнет из списка. Не исчезла (поиск по
 * всем спринтам показывает задачу и в новой неделе) — возвращается на место.
 */
const GONE_MS = 1500;

export interface SwipeView {
  offset: number;
  side: SwipeSide;
  /** Порог пройден: отпущенная строка уедет. */
  armed: boolean;
  /** `move` — за пальцем, `settle` — возвращается, `gone` — уехала. */
  phase: 'move' | 'settle' | 'gone';
  /** Где строка в списке: подложка рисуется ровно под ней. */
  top: number;
  height: number;
}

interface Gesture {
  x0: number;
  y0: number;
  mode: 'pending' | 'swipe' | 'scroll';
  width: number;
  top: number;
  height: number;
  armed: boolean;
}

export interface SwipeOptions {
  targets: SwipeTargets;
  /** Выключен, пока строку несут: жест захвата и свайп — один и тот же палец. */
  enabled: boolean;
  onCommit: (side: SwipeSide) => void;
  /** Палец ушёл вбок: клик, которым iOS закончит касание, надо погасить. */
  onGesture: () => void;
}

export function useSwipe(options: SwipeOptions): {
  ref: (element: HTMLElement | null) => void;
  view: SwipeView | undefined;
} {
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [view, setView] = useState<SwipeView | undefined>(undefined);
  // Последний показанный вид — для решений на отпускании. Читать его из
  // `setView(was => …)` нельзя: там побочные эффекты, а функцию-обновлятель
  // React в строгом режиме зовёт дважды — перенос случился бы дважды.
  const shown = useRef<SwipeView | undefined>(undefined);
  const show = useCallback((next: SwipeView | undefined) => {
    shown.current = next;
    setView(next);
  }, []);
  const latest = useRef(options);
  latest.current = options;
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const later = useCallback((ms: number, run: () => void) => {
    if (timer.current !== undefined) clearTimeout(timer.current);
    timer.current = setTimeout(run, ms);
  }, []);

  useEffect(() => () => clearTimeout(timer.current), []);

  useEffect(() => {
    if (element === null) return undefined;
    const row = element;
    let gesture: Gesture | undefined;

    function settle(): void {
      const from = shown.current;
      if (from === undefined) return;
      show({ ...from, offset: 0, armed: false, phase: 'settle' });
      later(SETTLE_MS, () => show(undefined));
    }

    function onStart(event: TouchEvent): void {
      const touch = event.touches[0];
      gesture = undefined;
      if (!latest.current.enabled || event.touches.length !== 1 || touch === undefined) return;
      // Касание в раскрытом меню спринта — выбор недели, а не жест строки.
      if (event.target instanceof Element && event.target.closest('.menu__list') !== null) return;

      gesture = {
        x0: touch.clientX,
        y0: touch.clientY,
        mode: 'pending',
        width: 0,
        top: 0,
        height: 0,
        armed: false,
      };
    }

    function onMove(event: TouchEvent): void {
      const touch = event.touches[0];
      if (gesture === undefined || gesture.mode === 'scroll' || touch === undefined) return;

      // Строку взяли долгим нажатием, пока палец стоял: дальше её несёт
      // перетаскивание.
      if (!latest.current.enabled) {
        gesture = undefined;
        show(undefined);
        return;
      }

      const dx = touch.clientX - gesture.x0;
      const dy = touch.clientY - gesture.y0;

      if (gesture.mode === 'pending') {
        if (Math.abs(dy) > LOCK_PX && Math.abs(dy) >= Math.abs(dx)) {
          gesture.mode = 'scroll';
          return;
        }
        if (Math.abs(dx) <= LOCK_PX) return;

        gesture.mode = 'swipe';
        gesture.width = row.offsetWidth;
        gesture.top = row.offsetTop;
        gesture.height = row.offsetHeight;
        latest.current.onGesture();
      }

      event.preventDefault();
      const side: SwipeSide = dx > 0 ? 'right' : 'left';
      const allowed = latest.current.targets[side] !== undefined;
      const armed = allowed && Math.abs(dx) >= commitDistance(gesture.width);

      // Отклик на пороге — «дотянул». На iPhone он может не прозвучать
      // (движение пальца — не нажатие, см. `haptic`); тогда остаётся смена
      // цвета подложки и двойной щелчок на отпускании.
      if (armed && !gesture.armed) haptic();
      gesture.armed = armed;

      show({
        offset: swipeOffset(dx, allowed),
        side,
        armed,
        phase: 'move',
        top: gesture.top,
        height: gesture.height,
      });
    }

    function onEnd(): void {
      const ended = gesture;
      gesture = undefined;
      if (ended?.mode !== 'swipe') return;

      latest.current.onGesture();
      const was = shown.current;
      if (was === undefined || !ended.armed) {
        settle();
        return;
      }

      // Отпускание — нажатие для iOS, и здесь щелчок звучит наверняка.
      haptic('strong');
      const away = (ended.width + 24) * (was.side === 'right' ? 1 : -1);
      show({ ...was, offset: away, phase: 'gone' });
      later(GONE_MS, settle);
      latest.current.onCommit(was.side);
    }

    function onCancel(): void {
      gesture = undefined;
      settle();
    }

    row.addEventListener('touchstart', onStart, { passive: true });
    row.addEventListener('touchmove', onMove, { passive: false });
    row.addEventListener('touchend', onEnd);
    row.addEventListener('touchcancel', onCancel);
    return () => {
      row.removeEventListener('touchstart', onStart);
      row.removeEventListener('touchmove', onMove);
      row.removeEventListener('touchend', onEnd);
      row.removeEventListener('touchcancel', onCancel);
    };
  }, [element, later, show]);

  return { ref: setElement, view };
}
